import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";
import { 
  User, 
  LogOut, 
  Settings, 
  MessageSquare, 
  Briefcase, 
  Star,
  Phone,
  InfoIcon,
  Home,
  BarChart3,
  Edit3,
  Image,
  Palette,
  FileText,
  Globe
} from "lucide-react";

// Import admin components
import Dashboard from "../components/admin/Dashboard";
import ContentEditor from "../components/admin/ContentEditor";
import MediaManager from "../components/admin/MediaManager";
import ContactInfoManager from "../components/admin/ContactInfoManager";
import ServicesManager from "../components/admin/ServicesManager";
import ProjectsManager from "../components/admin/ProjectsManager";
import TestimonialsManager from "../components/admin/TestimonialsManager";
import ContactSubmissionsManager from "../components/admin/ContactSubmissionsManager";
import AboutInfoManager from "../components/admin/AboutInfoManager";
import UserManagement from "../components/admin/UserManagement";

const AdminDashboard = () => {
  const { isAuthenticated, isLoading, adminUser, logout } = useAdmin();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({
    services: 0,
    projects: 0,
    testimonials: 0,
    submissions: 0
  });

  // Redirect if not authenticated
  if (!isLoading && !isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du panneau d'administration...</p>
        </div>
      </div>
    );
  }

  const menuItems = [
    { 
      id: "dashboard", 
      label: "Dashboard", 
      icon: BarChart3,
      description: "Vue d'ensemble" 
    },
    { 
      id: "content", 
      label: "Éditeur de contenu", 
      icon: Edit3,
      description: "Modifier tous les textes" 
    },
    { 
      id: "media", 
      label: "Gestionnaire de médias", 
      icon: Image,
      description: "Gérer photos et fichiers" 
    },
    { 
      id: "contact-info", 
      label: "Informations de contact", 
      icon: Phone,
      description: "Coordonnées" 
    },
    { 
      id: "about", 
      label: "À propos", 
      icon: InfoIcon,
      description: "Section À propos" 
    },
    { 
      id: "services", 
      label: "Services", 
      icon: Settings,
      description: "Gérer les services" 
    },
    { 
      id: "projects", 
      label: "Projets", 
      icon: Briefcase,
      description: "Portfolio de projets" 
    },
    { 
      id: "testimonials", 
      label: "Témoignages", 
      icon: Star,
      description: "Avis clients" 
    },
    { 
      id: "submissions", 
      label: "Demandes de contact", 
      icon: MessageSquare,
      description: "Messages reçus" 
    },
    { 
      id: "users", 
      label: "Utilisateurs", 
      icon: User,
      description: "Gestion des comptes" 
    }
  ];

  const renderActiveComponent = () => {
    switch (activeTab) {
      case "dashboard":
        return <Dashboard />;
      case "content":
        return <ContentEditor />;
      case "media":
        return <MediaManager />;
      case "contact-info":
        return <ContactInfoManager />;
      case "about":
        return <AboutInfoManager />;
      case "services":
        return <ServicesManager onStatsUpdate={(count) => setStats(prev => ({...prev, services: count}))} />;
      case "projects":
        return <ProjectsManager onStatsUpdate={(count) => setStats(prev => ({...prev, projects: count}))} />;
      case "testimonials":
        return <TestimonialsManager onStatsUpdate={(count) => setStats(prev => ({...prev, testimonials: count}))} />;
      case "submissions":
        return <ContactSubmissionsManager onStatsUpdate={(count) => setStats(prev => ({...prev, submissions: count}))} />;
      case "users":
        return <UserManagement />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-900 text-white p-2 rounded-lg">
                <Settings className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Administration - consultanttravaux.fr
                </h1>
                <p className="text-sm text-gray-600">
                  Consultant Travaux et Rénovation
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                <Home className="w-4 h-4" />
                <span>Voir le site</span>
              </a>
              
              <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-900 rounded-lg">
                <User className="w-4 h-4" />
                <span className="font-medium">{adminUser}</span>
              </div>
              
              <button
                onClick={logout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Navigation
              </h2>
              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-start space-x-3 px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                        activeTab === item.id
                          ? "bg-blue-50 text-blue-900 border border-blue-200"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{item.label}</div>
                        <div className="text-xs text-gray-500 mt-1">{item.description}</div>
                      </div>
                      {item.id === "submissions" && stats.submissions > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {stats.submissions}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Statistiques
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Services</span>
                  <span className="font-semibold text-blue-900">{stats.services}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Projets</span>
                  <span className="font-semibold text-blue-900">{stats.projects}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Témoignages</span>
                  <span className="font-semibold text-blue-900">{stats.testimonials}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Demandes</span>
                  <span className={`font-semibold ${stats.submissions > 0 ? 'text-red-600' : 'text-blue-900'}`}>
                    {stats.submissions}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {renderActiveComponent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;